import {
	Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges,
	SimpleChange, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, QueryList,
	ViewChild, ViewChildren, ElementRef
} from '@angular/core';
import { Device } from 'DiscoveryEngine.TS/Core';
import { Table } from 'primeng/table';
import { IfEntry } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { TableUtils } from 'common';


export class InterfaceSelectorEntry {
	public dataKey: string;
	public ifIndex: string;
	public ifAlias: string;
	public name: string;
	public description: string;
	public adminStatus: string;
	public operStatus: string;
	public poeStatus: string;
	public poeDetection: string;
	public connectedDevices: string;

	public ifIndexTag: number;
	public adminStatusTag: number;
	public operStatusTag: number;
	public poeStatusTag: number;
	public poeDetectionTag: number;

	public interfaceTag: IfEntry;
	public get hasInterfaceTag(): boolean {
		return !!this.interfaceTag;
	}

	constructor() {
		this.dataKey = "";

		this.ifIndex = "";
		this.ifAlias = "";
		this.name = "";
		this.description = "";
		this.adminStatus = "";
		this.operStatus = "";
		this.poeStatus = "";
		this.poeDetection = "";
		this.connectedDevices = "";

		this.ifIndexTag = -1;
		this.adminStatusTag = -1;
		this.operStatusTag = -1;
		this.poeStatusTag = -1;
		this.poeDetectionTag = -1;

		this.interfaceTag = null;
	}
}


@Component({
	selector: 'interface-selector',
	templateUrl: './interface-selector.component.html',
	styleUrls: ['./interface-selector.component.scss'],
	//changeDetection: ChangeDetectionStrategy.OnPush
})
export class InterfaceSelectorComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public device: Device;

	@Input()
	public selectionMode: string;

	@Input()
	public selection: IfEntry[];

	@Input()
	public showPoe: boolean;

	@Input()
	public ifTableHeightVh: number;

	@Input()
	public ifTableHeightPx: number;

	@Input()
	public ifTableMinHeight: number;

	@Output()
	public selectionChange: EventEmitter<IfEntry[]>;

	@ViewChild('table', { static: true })
	public table: Table;

	@ViewChildren('filter')
	public filters: QueryList<ElementRef<HTMLInputElement>>;

	private interfaces: IfEntry[];

	public cols: any[];
	public entries: InterfaceSelectorEntry[];

	public selectedInterfaces: any;

	public first: number;

	constructor(private changeDetector: ChangeDetectorRef) {

		this.device = null;
		this.selectionMode = "single";
		this.selection = [];
		this.showPoe = false;
		this.selectionChange = new EventEmitter<IfEntry[]>();
		this.interfaces = null;
		this.cols = [];

		this.entries = [];

		this.clearSelectedInterfaces();

		this.first = 0;
	}

	public get ifTableHeight(): string {
		let result = `max(calc(${this.ifTableHeightVh}vh + ${this.ifTableHeightPx}px), ${this.ifTableMinHeight}px)`;
		//console.log(result);
		return result;
	}

	public doCustomSort(event): void {
		let comparer = function (a: InterfaceSelectorEntry, b: InterfaceSelectorEntry): number {
			let result = 0;

			switch (event.field) {
				case 'ifIndex':
					if (a.ifIndexTag < b.ifIndexTag) {
						result = -1;
					}
					else if (a.ifIndexTag > b.ifIndexTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'adminStatus':
					if (a.adminStatusTag < b.adminStatusTag) {
						result = -1;
					}
					else if (a.adminStatusTag > b.adminStatusTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'operStatus':
					if (a.operStatusTag < b.operStatusTag) {
						result = -1;
					}
					else if (a.operStatusTag > b.operStatusTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'poeStatus':
					if (a.poeStatusTag < b.poeStatusTag) {
						result = -1;
					}
					else if (a.poeStatusTag > b.poeStatusTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'poeDetection':
					if (a.poeDetectionTag < b.poeDetectionTag) {
						result = -1;
					}
					else if (a.poeDetectionTag > b.poeDetectionTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	public ngOnInit(): void {
		this.cols = [
			{ field: 'ifIndex', header: 'IF Index', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ifAlias', header: 'IF Alias', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'adminStatus', header: 'Admin Status', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'operStatus', header: 'Oper Status', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];

		if (this.showPoe) {
			this.cols.push({ field: 'poeStatus', header: 'POE Status', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" });
			this.cols.push({ field: 'poeDetection', header: 'POE Detection', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" });
		}

		this.cols.push({ field: 'connectedDevices', header: 'Connected Devices', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" });
	}

	public onFilterChange(): void {
		this.clearSelectedInterfaces();
	}

	private clearSelectedInterfaces(): void {
		this.selectedInterfaces = (this.selectionMode == "single" ? undefined : []);

		this.selection = [];
		this.selectionChange.emit(this.selection);
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (!!changes["device"] || !!changes["selectionMode"]) {

			this.interfaces = [];
			if (!!this.device && this.device.HasIfTable) {
				this.interfaces = this.device.IfTable.ToArray();
			}

			this.clearSelectedInterfaces();

			this.first = 0;

			this.rebuildEntries();

			if (this.filters) {
				this.filters.forEach((filter: ElementRef<HTMLInputElement>) => {
					filter.nativeElement.value = "";
				});
			}

			this.cols.forEach((col) => {
				this.table.filter("", col.field, col.filterMatchMode)
			});
		}
	}

	public ngOnDestroy(): void {

	}

	public rebuildEntries(): void {

		let selectedIfIndexes = new Set();

		this.getSelectedInterfaces().forEach((ifEntry: IfEntry) => selectedIfIndexes.add(ifEntry.IfIndex));

		this.selectedInterfaces = undefined;
		this.entries = [];

		this.interfaces.forEach((ifEntry: IfEntry) => {
			let entry = new InterfaceSelectorEntry();

			entry.dataKey = ifEntry.IfIndex.toString();
			entry.ifIndex = ifEntry.IfIndex.toString();
			entry.ifAlias = ifEntry.Alias;
			entry.name = ifEntry.Name;
			entry.description = ifEntry.Description;
			entry.adminStatus = ifEntry.AdminStatusToString();
			entry.operStatus = ifEntry.OperStatusToString();
			entry.poeStatus = ifEntry.PoeAdminEnableToString();
			entry.poeDetection = ifEntry.PoeDetectionStatusToString();
			entry.connectedDevices = ifEntry.ConnectedDevices;

			entry.ifIndexTag = ifEntry.IfIndex;
			entry.adminStatusTag = ifEntry.AdminStatus;
			entry.operStatusTag = ifEntry.OperStatus;
			entry.poeStatusTag = ifEntry.PoeAdminEnable;
			entry.poeDetectionTag = ifEntry.PoeDetectionStatus;

			entry.interfaceTag = ifEntry;

			let addEntry = true;

			if (this.showPoe) {
				if (ifEntry.BridgePortIndex <= 0) {
					addEntry = false;
                }
			}

			if (addEntry) {
				this.entries.push(entry);

				if (selectedIfIndexes.has(ifEntry.IfIndex)) {
					// Add interface to selection
					switch (this.selectionMode) {
						case "single":
							this.selectedInterfaces = entry;
							break;
						case "multiple":
							if (this.selectedInterfaces === undefined) {
								this.selectedInterfaces = [entry];
							}
							else {
								this.selectedInterfaces.push(entry);
							}
							break;
						default:
							console.assert(false);
							break;
					}
				}
			}
		}, this);

		this.selection = this.getSelectedInterfaces();
		this.selectionChange.emit(this.selection);

		// Set column widths
		let colPropNames: string[] = [
			"ifIndex", "ifAlias", "name", "description", "adminStatus", "operStatus",
		];
		if (this.showPoe) {
			colPropNames.push("poeStatus");
			colPropNames.push("poeDetection");
		}
		colPropNames.push("connectedDevices");

		TableUtils.setColumnWidths(colPropNames, this.entries, this.cols);

		// Check for changes
		//this.changeDetector.markForCheck();
		this.changeDetector.detectChanges();
	}

	public onSelectionChange(event): void {
		this.selection = this.getSelectedInterfaces();
		this.selectionChange.emit(this.selection);
	}

	private getSelectedInterfaces(): IfEntry[] {
		let result: IfEntry[] = [];
		if (this.selectedInterfaces) {
			if (Array.isArray(this.selectedInterfaces)) {
				this.selectedInterfaces.forEach((entry: InterfaceSelectorEntry) => {
					result.push(entry.interfaceTag);
				}, this);
			}
			else {
				let entry = <InterfaceSelectorEntry>this.selectedInterfaces;
				result.push(entry.interfaceTag);
			}
		}
		return result;
	}

	public get totalInterfaceCount(): number {
		if (this.entries)
			return this.entries.length;
		else
			return 0;
	}

	public get selectedInterfaceCount(): number {
		if (this.selectedInterfaces) {
			if (Array.isArray(this.selectedInterfaces)) {
				return this.selectedInterfaces.length;
			}
			else {
				return 1;
			}
		}
		return 0;
	}
}
